import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Check, Search, Trash2, Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardConsigli() {
  const navigate = useNavigate();
  const [igPosts, setIgPosts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/consigli/instagram-all`, { headers });
      setIgPosts(res.data);
      const sel = new Set();
      res.data.forEach(p => { if (p.selected) sel.add(p.id); });
      setSelectedIds(sel);
    } catch {} finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveSelection = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/consigli/select`, { instagram_ids: [...selectedIds], category: 'consigli' }, { headers });
      toast.success(res.data.message);
      loadData();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const filteredPosts = igPosts.filter(p => {
    if (!filter) return true;
    return (p.caption || '').toLowerCase().includes(filter.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-consigli-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Consigli
            </h1>
            <p className="text-gray-600">Seleziona i post Instagram da mostrare nella pagina "Consigli"</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Filtra per parola chiave o hashtag..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="pl-10"
                  data-testid="consigli-filter-input"
                />
              </div>
              <Button
                onClick={saveSelection}
                disabled={saving}
                className="bg-[#0066b2] hover:bg-[#005494]"
                data-testid="consigli-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Salva Selezione ({selectedIds.size})
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Instagram className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nessun post Instagram trovato.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">{filteredPosts.length} post Instagram trovati</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredPosts.map((post, idx) => (
                  <div
                    key={post.id}
                    onClick={() => toggleSelect(post.id)}
                    className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      selectedIds.has(post.id)
                        ? 'border-[#0066b2] ring-2 ring-[#0066b2]/30 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`consigli-ig-post-${idx}`}
                  >
                    <img
                      src={post.thumbnail_url || post.media_url}
                      alt=""
                      className="w-full h-48 object-cover"
                      referrerPolicy="no-referrer"
                    />
                    {selectedIds.has(post.id) && (
                      <div className="absolute top-2 right-2 w-7 h-7 bg-[#0066b2] rounded-full flex items-center justify-center shadow">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-xs text-gray-600 line-clamp-3">{post.caption}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {post.timestamp ? new Date(post.timestamp).toLocaleDateString('it-IT') : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
